import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { SalesAssistant } from "@/components/SalesAssistant";
import { LeadFormDialog } from "@/components/LeadFormDialog";
import { QUAL_LABELS, BUCKET_STYLES, URGENCY_STYLES, fmtDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ArrowLeft, Pencil, Trash2, Calendar, AlertTriangle, Building2, ListChecks, Target, Globe, Mail, Phone } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

function qualDisplay(field, value) {
  const spec = QUAL_LABELS[field];
  if (spec && spec.options[value]) return spec.options[value];
  return value || "—";
}

function Row({ label, children }) {
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-border last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-navy text-right">{children || "—"}</span>
    </div>
  );
}

export default function LeadDetail({ role }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isAdmin = role === "admin";
  const [lead, setLead] = useState(null);
  const [config, setConfig] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [showEdit, setShowEdit] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/leads/${id}`);
      setLead(res.data);
    } catch {
      toast.error("Lead not found");
      navigate("/dashboard");
    }
  }, [id, navigate]);

  useEffect(() => { load(); api.get("/config").then((r) => setConfig(r.data)); }, [load]);

  const patch = async (fields) => {
    const res = await api.put(`/leads/${id}`, fields);
    setLead(res.data);
    toast.success("Updated");
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    const res = await api.post(`/leads/${id}/notes`, { text: noteText });
    setLead((l) => ({ ...l, notes: [res.data, ...(l.notes || [])] }));
    setNoteText("");
    toast.success("Note added");
  };

  const del = async () => {
    await api.delete(`/leads/${id}`);
    toast.success("Lead deleted");
    navigate("/dashboard");
  };

  if (!lead || !config) {
    return <div className="p-8 text-muted-foreground">Loading…</div>;
  }

  const q = lead.qualification || {};

  return (
    <div>
      <header className="bg-navy px-6 md:px-8 py-6 sticky top-0 z-10">
        <button onClick={() => navigate("/dashboard")} data-testid="back-button"
          className="flex items-center gap-2 text-slate-300 hover:text-gold text-sm mb-3 transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to pipeline
        </button>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="overline text-gold mb-1.5">{lead.lead_source}</p>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight leading-none">{lead.company_name}</h1>
            <p className="text-slate-300 text-sm mt-2">{lead.contact_name}{lead.title ? ` · ${lead.title}` : ""}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1.5 rounded text-sm font-medium border ${BUCKET_STYLES[lead.bucket] || "bg-slate-100"}`}>{lead.bucket}</span>
            <Button variant="outline" onClick={() => setShowEdit(true)} data-testid="edit-lead-button"
              className="bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white">
              <Pencil className="h-4 w-4 mr-2" /> Edit
            </Button>
            {isAdmin && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" data-testid="delete-lead-button"
                    className="bg-transparent border-white/20 text-white hover:bg-red-600 hover:border-red-600 hover:text-white">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this lead?</AlertDialogTitle>
                    <AlertDialogDescription>This permanently removes {lead.company_name} from the pipeline.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={del} data-testid="confirm-delete" className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
      </header>

      <div className="p-6 md:p-8 grid lg:grid-cols-3 gap-6">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Pipeline controls */}
          <div className="rounded-md border border-border bg-card p-5 grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="overline text-muted-foreground">Deal Stage</label>
              <Select value={lead.deal_stage} onValueChange={(v) => patch({ deal_stage: v })}>
                <SelectTrigger data-testid="deal-stage-select"><SelectValue /></SelectTrigger>
                <SelectContent>{config.deal_stages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="overline text-muted-foreground flex items-center gap-1"><Calendar className="h-3 w-3" /> Follow-up Date</label>
              <Input type="date" data-testid="follow-up-date"
                value={lead.follow_up_date ? lead.follow_up_date.slice(0, 10) : ""}
                onChange={(e) => patch({ follow_up_date: e.target.value })} />
            </div>
          </div>

          {/* Company info */}
          <Section icon={Building2} title="Company Information">
            <div className="grid sm:grid-cols-2 gap-x-8">
              <div>
                <Row label="Company name">{lead.company_name}</Row>
                <Row label="Contact name">{lead.contact_name}</Row>
                <Row label="Title">{lead.title}</Row>
                <Row label="Email">{lead.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{lead.email}</span>}</Row>
                <Row label="Phone">{lead.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{lead.phone}</span>}</Row>
              </div>
              <div>
                <Row label="Website">{lead.website && <span className="inline-flex items-center gap-1"><Globe className="h-3 w-3" />{lead.website}</span>}</Row>
                <Row label="CAGE code"><span className="font-mono">{lead.cage_code}</span></Row>
                <Row label="Users in CMMC scope">{lead.users_in_scope}</Row>
                <Row label="Lead source">{lead.lead_source}</Row>
              </div>
            </div>
          </Section>

          {/* Qualification */}
          <Section icon={ListChecks} title="Qualification Answers">
            <Row label={QUAL_LABELS.dod_contracts.label}>{qualDisplay("dod_contracts", q.dod_contracts)}</Row>
            <Row label={QUAL_LABELS.asked_about_requirements.label}>{qualDisplay("asked_about_requirements", q.asked_about_requirements)}</Row>
            <Row label={QUAL_LABELS.handles_cui.label}>{qualDisplay("handles_cui", q.handles_cui)}</Row>
            <Row label={QUAL_LABELS.ssp_poam.label}>{qualDisplay("ssp_poam", q.ssp_poam)}</Row>
            <Row label="Environment (email/files/collab)">{q.environment}</Row>
            <Row label={QUAL_LABELS.self_assessment.label}>{qualDisplay("self_assessment", q.self_assessment)}</Row>
            <Row label={QUAL_LABELS.timeline.label}>{qualDisplay("timeline", q.timeline)}</Row>
            <Row label="Users in scope">{q.users_in_scope || lead.users_in_scope}</Row>
            <Row label="Who owns CMMC readiness">{q.who_owns_readiness}</Row>
            <Row label={QUAL_LABELS.decision_maker_access.label}>{qualDisplay("decision_maker_access", q.decision_maker_access)}</Row>
            <div className="pt-3">
              <span className="text-sm text-muted-foreground">Biggest concern</span>
              <p className="text-sm text-navy mt-1 leading-relaxed">{q.biggest_concern || "—"}</p>
            </div>
          </Section>

          {/* Sales Assistant */}
          <SalesAssistant leadId={lead.id} assistant={lead.assistant}
            onGenerated={(a) => setLead((l) => ({ ...l, assistant: a }))} />
        </div>

        {/* Right column */}
        <div className="space-y-6">
          {/* Scoring output */}
          <Section icon={Target} title="Scoring Output">
            {isAdmin && (
              <div className="flex items-center justify-between py-2 border-b border-border">
                <span className="text-sm text-muted-foreground">Lead score</span>
                <span className="font-display text-3xl font-bold text-navy">{lead.score ?? "—"}</span>
              </div>
            )}
            <Row label="Lead bucket">
              <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${BUCKET_STYLES[lead.bucket] || ""}`}>{lead.bucket}</span>
            </Row>
            <div className="py-2 border-b border-border">
              <span className="text-sm text-muted-foreground">Recommended offer</span>
              <p className="text-sm font-medium text-navy mt-1">{lead.recommended_offer}</p>
              {lead.offer_label && <p className="text-xs text-gold-dark mt-0.5">{lead.offer_label}</p>}
            </div>
            <Row label="Urgency">
              <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${URGENCY_STYLES[lead.urgency] || ""}`}>{lead.urgency}</span>
            </Row>
            <div className="py-2 border-b border-border">
              <span className="text-sm text-muted-foreground">Next action</span>
              <p className="text-sm font-medium text-navy mt-1">{lead.next_action}</p>
            </div>
            {lead.red_flags && lead.red_flags.length > 0 && (
              <div className="py-2 border-b border-border">
                <span className="text-sm text-red-700 flex items-center gap-1 mb-1"><AlertTriangle className="h-3.5 w-3.5" /> Red flags</span>
                <ul className="list-disc pl-5 text-sm text-red-700 space-y-0.5">
                  {lead.red_flags.map((f, i) => <li key={i}>{f}</li>)}
                </ul>
              </div>
            )}
            {isAdmin && lead.reason && (
              <div className="pt-3">
                <span className="text-sm text-muted-foreground">Reason for recommendation</span>
                <p className="text-sm text-slate-700 mt-1 leading-relaxed">{lead.reason}</p>
              </div>
            )}
            {!isAdmin && (
              <p className="text-[11px] text-muted-foreground pt-3 italic">Full scoring logic is available to Admins only.</p>
            )}
          </Section>

          {/* Notes */}
          <Section icon={Pencil} title="Notes">
            <div className="space-y-2 mb-4">
              <Textarea data-testid="note-input" rows={3} placeholder="Add a note…" value={noteText}
                onChange={(e) => setNoteText(e.target.value)} />
              <Button data-testid="add-note-button" onClick={addNote} size="sm"
                className="bg-navy text-white hover:bg-navy-700 w-full">Add Note</Button>
            </div>
            <div className="space-y-3">
              {(lead.notes || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No notes yet.</p>
              ) : (
                lead.notes.map((n) => (
                  <div key={n.id} className="rounded-md bg-slate-50 border border-border p-3" data-testid="note-item">
                    <p className="text-sm text-navy leading-relaxed">{n.text}</p>
                    <p className="text-[11px] text-muted-foreground mt-1.5">{n.author} · {fmtDate(n.created_at)}</p>
                  </div>
                ))
              )}
            </div>
          </Section>
        </div>
      </div>

      <LeadFormDialog open={showEdit} onOpenChange={setShowEdit} config={config} initial={lead} onSaved={load} />
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="rounded-md border border-border bg-card">
      <div className="flex items-center gap-2 px-5 py-3.5 border-b border-border">
        <Icon className="h-4 w-4 text-gold-dark" aria-hidden="true" />
        <h3 className="font-display font-bold text-navy leading-none">{title}</h3>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}
